import type { DataGap, LengthUnit, RawProfilePoint, TrimResult } from '@/types/profile';
import { MM_PER_INCH, detectUnit } from '@/lib/utils/units';

/**
 * Automatically detects and removes CNC lathe lead-in / lead-out movements,
 * leaving only the physical bat profile.
 *
 * Why this is needed:
 * A CNC lathe program doesn't start the tool already touching the stock. It
 * rapids the tool to a safe clearance position, approaches the part, cuts the
 * full profile, then retracts. Those approach/retract moves get logged into
 * the same .C file as the cutting pass and must never be turned into
 * geometry — they are not part of the bat.
 *
 * Heuristic (documented so it's auditable, not a black box):
 *
 * 1. LONGEST MONOTONIC RUN
 *    The physical profile is cut in a single continuous pass, so `position`
 *    moves consistently in one direction from knob-end to tip-end (or vice
 *    versa). Lead-in/out moves break that pattern: they are either a
 *    reversal in direction, or an implausible jump in radius (the tool
 *    leaving the stock for clearance, or re-entering it). Position-step
 *    SIZE is deliberately not used as a signal, since real CNC exports use
 *    adaptive point spacing — long, straight sections of the handle/barrel
 *    are sampled with far fewer points than curved transitions, so a big
 *    gap in position is completely normal mid-profile. We find the longest
 *    run of consecutive points (in file order) whose direction stays
 *    consistent and whose radius never jumps implausibly, measured by
 *    physical position span (not point count, for the same adaptive-spacing
 *    reason). That run is the candidate physical profile.
 *
 * 2. EDGE RADIUS SANITY TRIM
 *    Even within the monotonic run, a machine sometimes logs a couple of
 *    "settling" points right at the very start/end at clearance radius
 *    (larger than the rest of the part). We trim points off just the two
 *    edges of the candidate run while they are radius outliers relative to
 *    the run's interquartile range — never from the interior, so we can't
 *    accidentally cut into the real barrel/knob.
 *
 * 3. CONFIDENCE SCORE
 *    Reported to the UI (0..1) based on how much of the file was kept and
 *    how "clean" the monotonic run was, so operators can sanity-check
 *    unusual files at a glance without ever having to trim manually.
 */

interface Run {
  start: number; // inclusive index into points[]
  end: number; // inclusive index into points[]
  direction: 1 | -1;
}

function quantile(sortedValues: number[], q: number): number {
  if (sortedValues.length === 0) return 0;
  const pos = (sortedValues.length - 1) * q;
  const base = Math.floor(pos);
  const rest = pos - base;
  if (sortedValues[base + 1] !== undefined) {
    return sortedValues[base] + rest * (sortedValues[base + 1] - sortedValues[base]);
  }
  return sortedValues[base];
}

/**
 * No wood baseball bat — barrel, knob, anything — comes anywhere close to
 * this radius. Real bats top out around 35mm radius even on oversized
 * barrels; lathe clearance/rapid-traverse positions are typically 100mm+.
 * This generous absolute ceiling is deliberately NOT derived from the
 * file's own data: a percentile-based ceiling gets contaminated when the
 * lead-in/out fraction of a file is larger than assumed (its own extreme
 * points pull the percentile up), silently letting clearance points back
 * in. A fixed, domain-informed bound has no such failure mode.
 */
const ABSOLUTE_MAX_PLAUSIBLE_RADIUS_MM = 55;

/**
 * Finds the longest run of consecutive points that form a single continuous
 * cutting pass: position moves consistently in one direction, and radius
 * stays within what's physically plausible for the part.
 *
 * Real CNC .C exports use ADAPTIVE point spacing: many closely-spaced points
 * where the profile curves, and far fewer, widely-spaced points where the
 * radius stays constant (a straight section of the handle or barrel). That
 * means a large gap in `position` between two adjacent points is completely
 * normal mid-profile — it does NOT indicate a rapid-traverse move — so
 * position-step size is deliberately NOT used to break a run.
 *
 * Radius is checked two ways, because one test alone misses real cases:
 *
 *  - ABSOLUTE ceiling: any point whose radius is nowhere near physically
 *    possible for a bat (see constant above) is excluded outright, however
 *    gradually the data approaches it. This is what correctly excludes a
 *    multi-point lead-in ramp without also excluding a genuinely sharp part
 *    feature like a knob-to-handle transition — that transition's radius
 *    values stay well within plausible range even though they change fast.
 *
 *  - LOCAL step test: catches a modest clearance value that ISN'T caught by
 *    the absolute ceiling (e.g. only ~50% larger than the real barrel) but
 *    IS an abrupt, isolated jump relative to the fine, smooth steps of an
 *    actual taper. The typical step size is computed only from points
 *    already under the absolute ceiling, so a small number of such values
 *    can't meaningfully skew it, and the multiplier is generous enough that
 *    a real sharp taper feature (which still fits within a bounded multiple
 *    of the local step size) isn't mistaken for one.
 *
 * "Longest" is measured by physical position SPAN, not point count — with
 * adaptive spacing, a short curved region can easily contain more points
 * than a long straight one, so counting points would systematically favor
 * the wrong segment.
 */
function findLongestMonotonicRun(points: RawProfilePoint[], unit: LengthUnit): Run | null {
  if (points.length < 3) return null;

  const absoluteCeiling =
    unit === 'in' ? ABSOLUTE_MAX_PLAUSIBLE_RADIUS_MM / MM_PER_INCH : ABSOLUTE_MAX_PLAUSIBLE_RADIUS_MM;
  const isPlausible = points.map((p) => p.radius <= absoluteCeiling);

  const posDeltas = points.slice(1).map((p, i) => p.position - points[i].position);
  const radDeltas = points.slice(1).map((p, i) => p.radius - points[i].radius);

  // Values smaller than this are treated as representation/arc-math noise,
  // not a genuine signal — source coordinates (and interpolated arc points)
  // are only meaningful to about 3-4 decimal places, so a "reversal" or
  // "step" smaller than this is never real machine movement.
  const NOISE_EPSILON = 1e-3;

  // Typical step size among points that are already known to be plausible —
  // this keeps a handful of modest-but-still-not-real clearance values from
  // meaningfully skewing what counts as "normal."
  const plausibleStepSizes: number[] = [];
  for (let i = 0; i < radDeltas.length; i++) {
    if (isPlausible[i] && isPlausible[i + 1]) {
      const d = Math.abs(radDeltas[i]);
      if (d > NOISE_EPSILON) plausibleStepSizes.push(d);
    }
  }
  const sortedSteps = [...plausibleStepSizes].sort((a, b) => a - b);
  const medianStep = sortedSteps.length > 0 ? quantile(sortedSteps, 0.5) : 1e-6;
  const maxReasonableStep = Math.max(medianStep * 12, medianStep + 4);

  let bestRun: Run | null = null;
  let curStart = 0;
  let curDir: 1 | -1 | 0 = 0;

  const span = (run: Run) => Math.abs(points[run.end].position - points[run.start].position);

  const flush = (endIndexExclusive: number) => {
    if (curDir === 0) return;
    const candidate: Run = { start: curStart, end: endIndexExclusive - 1, direction: curDir };
    if (candidate.end <= candidate.start) return;
    if (!bestRun || span(candidate) > span(bestRun)) bestRun = candidate;
  };

  for (let i = 0; i < posDeltas.length; i++) {
    const d = posDeltas[i];
    const dir: 1 | -1 | 0 = d > NOISE_EPSILON ? 1 : d < -NOISE_EPSILON ? -1 : 0;
    const withinCeiling = isPlausible[i] && isPlausible[i + 1];
    const withinLocalStep = Math.abs(radDeltas[i]) <= maxReasonableStep;
    const radOk = withinCeiling && withinLocalStep;

    if (dir === 0) {
      // Duplicate/near-duplicate position — treat as continuation of current run,
      // unless the radius itself jumped implausibly.
      if (!radOk) {
        flush(i + 1);
        curDir = 0;
      }
      continue;
    }

    if (curDir === 0) {
      if (radOk) {
        curDir = dir;
        curStart = i;
      }
    } else if (dir !== curDir || !radOk) {
      flush(i + 1);
      curDir = radOk ? dir : 0;
      curStart = i + 1;
    }
  }
  flush(posDeltas.length + 1);

  return bestRun;
}

/**
 * Trims radius-outlier points from just the two edges of a run (never the
 * interior). This is a light secondary safety net for the odd leftover
 * "settling" point right at a boundary — deliberately loose (a wide IQR
 * multiplier, capped point count) so it can't nibble into genuine knob or
 * tip geometry, since the global plausibility ceiling in
 * findLongestMonotonicRun already does the heavy lifting.
 */
function trimEdgeOutliers(
  points: RawProfilePoint[],
  run: Run,
): { start: number; end: number } {
  const radii = points.slice(run.start, run.end + 1).map((p) => p.radius).sort((a, b) => a - b);
  const q1 = quantile(radii, 0.25);
  const q3 = quantile(radii, 0.75);
  const iqr = q3 - q1 || 1e-6;
  const upperFence = q3 + 3 * iqr;
  const lowerFence = Math.max(0, q1 - 3 * iqr);

  let start = run.start;
  let end = run.end;

  // Only trim a small number of points from each edge — a real bat tip can
  // legitimately have a small radius, and a real knob can legitimately be
  // wide, so we cap how far this is allowed to eat into the run.
  const maxTrimEach = Math.max(1, Math.floor((run.end - run.start) * 0.015));

  let trimmedFromStart = 0;
  while (
    start < end &&
    trimmedFromStart < maxTrimEach &&
    (points[start].radius > upperFence || points[start].radius < lowerFence)
  ) {
    start += 1;
    trimmedFromStart += 1;
  }

  let trimmedFromEnd = 0;
  while (
    end > start &&
    trimmedFromEnd < maxTrimEach &&
    (points[end].radius > upperFence || points[end].radius < lowerFence)
  ) {
    end -= 1;
    trimmedFromEnd += 1;
  }

  return { start, end };
}

/** A gap must span at least this much position, AND the implied slope
 * (radius change per unit position) across it must be steep — that
 * combination is what distinguishes genuinely missing geometry from an
 * ordinary sparse-but-shallow straight section. A flat radius threshold
 * alone isn't enough: G-code sources (like a parsed .TUR file) legitimately
 * have long, sparse straight moves with a real but shallow taper — e.g. 74mm
 * of position for under 2mm of radius change is just a normal G1 line, not
 * missing data. A genuine gap (a knob or cup skipped entirely) has a slope
 * many times steeper than any real taper on a bat. */
const GAP_POSITION_THRESHOLD_MM = 15;
const GAP_SLOPE_THRESHOLD = 0.5; // radius-units per position-unit

/**
 * Flags suspected missing-geometry regions: a large jump in position paired
 * with an implausibly steep implied slope in radius. That combination means
 * a curve must have existed there that simply wasn't recorded, most likely
 * because it was shaped by a separate tool pass (e.g. a knob-forming or cup
 * routing operation) not captured in this export.
 */
function detectDataGaps(
  valid: RawProfilePoint[],
  rangeStart: number,
  rangeEnd: number,
  unit: LengthUnit,
): DataGap[] {
  const posThreshold =
    unit === 'in' ? GAP_POSITION_THRESHOLD_MM / MM_PER_INCH : GAP_POSITION_THRESHOLD_MM;

  const sorted = [...valid].sort((a, b) => a.position - b.position);
  const gaps: DataGap[] = [];

  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i];
    const b = sorted[i + 1];
    const posGap = b.position - a.position;
    const radGap = Math.abs(b.radius - a.radius);
    const slope = posGap > 0 ? radGap / posGap : 0;
    if (posGap <= posThreshold || slope <= GAP_SLOPE_THRESHOLD) continue;
    // Only report gaps that touch the physical profile we actually kept —
    // a gap entirely inside the discarded lead-in/out region isn't actionable.
    if (b.position < rangeStart - posThreshold || a.position > rangeEnd + posThreshold) continue;
    gaps.push({ fromPosition: a.position, toPosition: b.position, fromRadius: a.radius, toRadius: b.radius });
  }

  return gaps;
}

export function trimProfile(points: RawProfilePoint[]): TrimResult {
  const notes: string[] = [];

  // Basic validity filter first: negative radius is physically impossible.
  const valid = points.filter((p) => p.radius >= 0);
  const rejected = points.filter((p) => p.radius < 0);
  if (rejected.length > 0) {
    notes.push(`Rejected ${rejected.length} row(s) with negative radius (invalid data).`);
  }

  if (valid.length < 3) {
    notes.push('Not enough valid points to detect a profile.');
    return {
      trimmedPoints: valid,
      leadIn: [],
      leadOut: [],
      rejected,
      gaps: [],
      notes,
      confidence: 0,
    };
  }

  const unit: LengthUnit = detectUnit(valid);
  const run = findLongestMonotonicRun(valid, unit);
  if (!run) {
    notes.push('Could not detect a continuous cutting pass; using all points unmodified.');
    return {
      trimmedPoints: valid,
      leadIn: [],
      leadOut: [],
      rejected,
      gaps: [],
      notes,
      confidence: 0.3,
    };
  }

  const { start, end } = trimEdgeOutliers(valid, run);

  const leadIn = valid.slice(0, start);
  const trimmedPoints = valid.slice(start, end + 1);
  const leadOut = valid.slice(end + 1);

  if (leadIn.length > 0) {
    notes.push(`Removed ${leadIn.length} lead-in point(s) before the physical profile.`);
  }
  if (leadOut.length > 0) {
    notes.push(`Removed ${leadOut.length} lead-out point(s) after the physical profile.`);
  }
  notes.push(
    `Kept ${trimmedPoints.length} of ${valid.length} points as the physical bat profile (${run.direction === 1 ? 'ascending' : 'descending'} pass).`,
  );

  const keptFraction = trimmedPoints.length / valid.length;
  const runCleanliness = (run.end - run.start + 1) / valid.length;
  const confidence = Math.max(0, Math.min(1, keptFraction * 0.6 + runCleanliness * 0.4));

  const rangeStart = trimmedPoints[0]?.position ?? 0;
  const rangeEnd = trimmedPoints[trimmedPoints.length - 1]?.position ?? 0;
  const gaps = detectDataGaps(valid, rangeStart, rangeEnd, unit);

  return { trimmedPoints, leadIn, leadOut, rejected, gaps, notes, confidence };
}
